import { Auth } from "aws-amplify";
import React, { useState } from "react";
import styled from "styled-components";

import { assertNativeError } from "@examspark/common/dist/utils";
import {
  Button,
  Form,
  TextInput,
  Toolbar,
  VSpace,
  Link,
} from "@examspark/common-ui";
import {
  Banner,
  FailureBanner,
  SuccessBanner,
} from "@examspark/common-ui/dist/components/banners/Banner";
import { PasswordInput } from "@examspark/common-ui/dist/components/inputs/TextInput";

import { useLogout } from "../../hooks/useLogout";

const Styled = styled.div`
  h1 {
    margin: 0 0 2rem;
    font-weight: normal;
    color: #333;
    text-align: center;
  }
`;

const ResetPassword = (props: {
  header?: React.ReactNode;
  onBackToLogin?: () => void;
}) => {
  const [state, setState] = useState<"code" | "password" | "submit">("code");

  // first screen
  const [email, setEmail] = useState("");

  // second screen
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // both
  const [error, setError] = useState<Error | null>(null);

  const logout = useLogout();

  const handleCode = async () => {
    setError(null);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const forgotData = await Auth.forgotPassword(normalizedEmail);
      console.log({ forgotData });
      setState("password");
    } catch (error: any) {
      assertNativeError(error);
      console.log({ error });
      setError(error);
    }
  };

  const handleReset = async () => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const forgotData = await Auth.forgotPasswordSubmit(
        normalizedEmail,
        code,
        newPassword,
      );
      console.log({ forgotData });
    } catch (error: any) {
      assertNativeError(error);
      console.log({ error });
      setError(error);
    } finally {
      setState("submit");
    }
  };

  const handleSkipCode = async () => {
    setError(null);
    setState("password");
  };

  return (
    <Styled>
      {props.header}
      <h1>Reset Password</h1>
      <Form>
        <Form.FieldList>
          {error && <FailureBanner>{error?.message}</FailureBanner>}
          {state === "code" && (
            <>
              <Form.Field label="Email Address">
                <TextInput
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      handleCode();
                    }
                  }}
                />
              </Form.Field>
              <Toolbar>
                <Button title="I have a code" stretch onClick={handleSkipCode}>
                  I have a code
                </Button>
                <Button
                  color="primary"
                  title="Send Code"
                  variant="contained"
                  stretch
                  onClick={handleCode}
                >
                  Send Reset Code
                </Button>
              </Toolbar>
            </>
          )}
          {state === "password" && (
            <VSpace size="3rem">
              <Banner>
                We've sent you an email containing a verification code. Please
                enter that code in the field below, then choose and confirm your
                new password.
              </Banner>
              <VSpace>
                <Form.Field label="Verification Code">
                  <TextInput
                    name="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </Form.Field>
                <Form.Field label="New Password">
                  <PasswordInput
                    name="password"
                    value={password}
                    showPassword={showPassword}
                    onShowPasswordChange={() => setShowPassword(!showPassword)}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Form.Field>
                <Form.Field label="Confirm New Password">
                  <PasswordInput
                    name="newPassword"
                    value={newPassword}
                    showPassword={showPassword}
                    onShowPasswordChange={() => setShowPassword(!showPassword)}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </Form.Field>
                <Toolbar>
                  <Button title="Back" stretch onClick={() => setState("code")}>
                    Back
                  </Button>
                  <Button
                    color="primary"
                    title="Reset Password"
                    variant="contained"
                    stretch
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Toolbar>
              </VSpace>
            </VSpace>
          )}
          {state === "submit" && !error && (
            <SuccessBanner>
              Your password has been reset. Please login with your new password.
            </SuccessBanner>
          )}

          <Link onClick={() => logout().then(() => props.onBackToLogin?.())}>
            Back to Login
          </Link>
        </Form.FieldList>
      </Form>
    </Styled>
  );
};

export default ResetPassword;
